import { gql } from '@apollo/client';

export const GET_VACANCY = gql`
  query getVacancy($id: ID!) {
    vacancy(id: $id, include: $include)
      @rest(
        type: "Vacancy"
        path: "/v4/vacancies/{args.id}?include={args.include}"
      ) {
      id
      key
      title
      label
      published
      name
      status
      spend_status
      customer_id
      vacancy_url
      company
      budget
      customer_type
      created_at
      has_custom_url
      language_id
      typeform_url
      brand_id
      salary_min
      salary_max
      currency
      pay_period
      salary_note
      salary_metadata
      location
      channels
      templates
      has_custom_url
      lead_recruiter {
        id
        firstname
      }
      brand {
        id
        name
        slug
        default_primary_color
        default_secondary_color
        default_tertiary_color
        default_recruiter
        default_language_id
        default_logo
        default_creatives
        default_locations
        default_facebook_page_id
        default_display_link
        default_banner_title
        default_cta
        default_accent_color
        salary_mandatory
        facebookPage {
          id
          customer_id
          channel_id
          ig_channel_id
          name
          logo_url
          status
        }
      }
      targeting {
        brand_id
        job_type
        job_type_name
        sub_job_type
        sub_job_type_name
        experience
        vac_name
        locations
        language_id
        locations {
          name
          country_code
        }
      }
    }
  }
`;

export const GET_VACANCY_ADS = gql`
  query getVacancyAds($vacancyId: ID!) {
    ads(id: $vacancyId)
      @rest(
        type: "[Ad]"
        path: "/v4/vacancies/{args.id}/ads?include=adMetric"
      ) {
      id
      priority
      channel
      name
      status
      currency
      stencil
      file_type
      thumbnail_url
      placement
      adMetric @type(name: "AdMetric") {
        applications
        impressions
        spend
        visits
        leads
        apmi
        convs3075
        clicks
      }
    }
  }
`;

export const GET_VACANCY_METRICS = gql`
  query getVacancyMetrics($vacancyId: ID!, $days: Int, $channel: String) {
    metrics(id: $vacancyId, days: $days, channel: $channel)
      @rest(
        type: "VacancyMetric"
        path: "/v4/vacancies/{args.id}?include=vacMetrics,vacBudget,vacSpent,vacApplications&filter_days={args.days}&filter_channel={args.channel}"
      ) {
      status
      spend_status
      vacBudget {
        total_budget
      }
      vacSpent
      vacApplications
      vacMetrics @type(name: "[Metrics]") {
        applications
        date
        impressions
        visits
        leads
        spend
        clicks
      }
    }
  }
`;

export const GET_VACANCY_BUDGET = gql`
  query getVacancyBudget($vacancyId: ID!) {
    budget(id: $vacancyId)
      @rest(
        type: "VacancyBudget"
        path: "/v4/vacancies/{args.id}?include=vacBudget"
      ) {
      vacBudget {
        total_budget
      }
    }
  }
`;

// SYM-5391. Only the two lock flags, read when the editor opens and again whenever a video
// entry point is pressed, so a tab that has been open a while cannot start a duplicate. The
// field must be named here or Apollo strips it, even though the API returns it on every vacancy.
export const GET_VACANCY_VIDEO_LOCKS = gql`
  query getVacancyVideoLocks($vacancyId: ID!) {
    vacancyVideoLocks(id: $vacancyId)
      @rest(type: "VacancyVideoLocks", path: "/v4/vacancies/{args.id}") {
      video_generation_locks {
        creatomate
        fal
      }
    }
  }
`;

export const GET_EDITOR_DATA = gql`
  query getEditorData($vacancy: ID!) {
    editorData(vacancy: $vacancy)
      @rest(
        type: "AdEditorData"
        path: "/v4/vacancies/{args.vacancy}?include=adVariants"
      ) {
      adVariants {
        id
        company
        description
        headline
        display_link
        text
        path
        logo
        function
        cta
        placement
        creative_type
        svg
        config
        # SYM-5564. Same trap as the SYM-5797 fields below: the user's saved focus rectangle was
        # stripped here, so Bring-to-Life saw an undefined crop on every load and animated the
        # original framing instead of the focus the user chose.
        crop
        # SYM-6076. Marks a Bring to Life clip the signed-in person has never looked at, so the
        # editor can badge it. Same trap as everything around it: ask, or Apollo strips it.
        has_unseen_clip
        # SYM-5797. A field absent from this selection set is STRIPPED by Apollo even when the API
        # returns it, so the editor loaded a v2 variant with no template and fell back to the
        # legacy overlay -- the choice looked lost after navigating away and back. Exposing the
        # fields on the transformer was necessary and not sufficient; both layers had to ask.
        template
        accent_color
        # SYM-6185. The same trap once more: without asking here the server's "this video is a
        # Bring to Life clip" never reaches the editor, and the sparkle on a saved clip stays live.
        is_bring_to_life_clip
        free_text
        subtext
        salary
        benefits
        # SYM-6301. The customer's logo choice. Without asking here a reload lights the design's
        # own place however the ad was set -- the choice looks lost although the server kept it.
        logo_place
        # SYM-5811. Write-only for as long as they have existed: the API did not return them and
        # this selection set did not ask, so nothing could read what colour an ad actually uses.
        # primary_color is the one that matters most -- AbstractTemplateRenderer::primary() reads
        # the VARIANT's copy and never the brand's, so it decides what every template is painted
        # with. banner_title is the same defect on the WANTED badge.
        primary_color
        # SYM-5929: what the ad is actually drawn with, so the picker can stop showing black.
        resolved_primary_color
        resolved_accent_color
        secondary_color
        tertiary_color
        banner_title
        # SYM-6214. The pictures the server drew on save, so the editor asks for nothing in the
        # normal case. Same stripping trap as SYM-5797 above: the API returning them is not enough,
        # this selection set has to ask or Apollo drops them and every ad looks unprepared -- which
        # would silently put the whole panel back on the live path this ticket exists to remove.
        preview_url
        template_previews
        shipped_preview_url
        preview_pending
        preview_state
        preview_generation
      }
    }
  }
`;

export const GET_VACANCY_CHECKLIST = gql`
  query getChecklist($id: ID!) {
    checklist(id: $id)
      @rest(
        type: "VacancyChecklistItem"
        path: "/v4/vacancies/{args.id}/checklist"
      ) {
      key
      label
      completed
    }
  }
`;

export const GET_TARGETING_CONFIG = gql`
  query getTargetingConfig($vacancyId: ID!) {
    targetingConfig(vacancy: $vacancyId)
      @rest(
        type: "TargetingConfiguration"
        path: "/v4/vacancies/{args.vacancy}?include=targeting"
      ) {
      published
      targeting {
        job_type
        sub_job_type
        experience
        vac_name
        locations
        language_id
        brand_id
      }
    }
  }
`;

export const GET_PREFERENCES_CONFIG = gql`
  query getPreferencesConfig($vacancyId: ID!) {
    preferencesConfig(vacancy: $vacancyId)
      @rest(
        type: "PreferencesConfiguration"
        path: "/v4/vacancies/{args.vacancy}?include=notificationPreferences,vacancyTemplates,integrationEntities"
      ) {
      name
      lead_recruiter_id
      excluded_from_careers_page
      published
      notificationPreferences {
        email_on_candidate
        email_on_communication
      }
      vacancyTemplates {
        enabled
        done_survey
        ask_for_cv
        got_cv
      }
      integrationEntities {
        id
        external_id
        integration {
          type
          friendly_name
          logo_url
          icon_url
        }
        job_info {
          id
          name
          status
          job_code
        }
      }
    }
  }
`;

export const GET_VACANCY_COPY = gql`
  query getVacancyCopy($vacancyId: ID!) {
    vacCopy(id: $vacancyId)
      @rest(type: "VacancyCopy", path: "/v4/vacancies/{args.id}/copy") {
      primary_color
      secondary_color
      tertiary_color
      company_title
      expectations_title
      id
      pictures
      job_title
      offer_title
      page_cta
      procedure_text
      procedure_title
      title
      todo_title
      vac_text_company
      vac_text_essence
      vac_text_expectations
      vac_text_offer
      vac_text_todo
      salary_min
      salary_max
      currency
      pay_period
      salary_note
      salary_metadata
      vac_text_uniqueness @type(name: "[VacancyCopyUniqueness]") {
        value
        icon
      }
      vac_url
      share_url
      url
    }
  }
`;

export const GET_VACANCY_SURVEY = gql`
  query getVacancySurvey($vacancyId: ID!) {
    vacSurvey(id: $vacancyId)
      @rest(type: "[Vacancy]", path: "/v4/vacancies/{args.id}/survey") {
      id
      logo_id
      primary_color
      secondary_color
      questions @type(name: "[FormQuestion]") {
        custom
        editable
        index
        label
        properties
        ref
        required
        type
      }
    }
  }
`;

export const GET_VACANCY_TRAJECTORY_PACE = gql`
  query getPace($vacancyId: ID!) {
    getPace(id: $vacancyId)
      @rest(path: "/v4/vacancies/{args.id}/pace", method: "GET") {
      pace
    }
  }
`;

export const GET_VACANCY_CHANNELS = gql`
  query getVacancyChannels($id: ID!) {
    vacancy(id: $id) @rest(type: "Vacancy", path: "/v4/vacancies/{args.id}") {
      key
      channels
    }
  }
`;
